// Idle pre-warm of the desks a person uses (shell-ia §15.5 F3; target: a warm desk opens in ≤ 150 ms). WP-DESK-HOST.
//
// Two seconds after the first screen's largest paint, at idle, up to two desks open out of view in the kept-frame pool: the person's pinned
// desks first (the `pinned` section of the viewer's `workspaces` layout, `usePinnedDesks`), then the desks they opened most in the last 14 days (`GET /api/econic/me/recent?kind=desk&take=2`, WP-SHELL). Only where the
// device can spare it (≥ 1024 px, no data saver, ≥ 4 GB, `canWarm()` in KeptPages), never while the person types, and once per sign-in.
// A warmed session records a "warm"; the open is recorded the first time the desk is in view (`POST /api/pages/opened`), so usage stays honest.
import { useEffect, useRef } from "react";
import { useApi, useAuth } from "@ncp/api-client";
import { usePinnedDesks } from "../workspaces/pinned";

export interface RecentDesk {
  pageKey: string;
  space: string | null;
  opens?: number;
  lastAt?: string;
}

const START_AFTER_LCP_MS = 2000;
const FALLBACK_START_MS = 4500;
const TYPING_PAUSE_MS = 2500;
export const WARM_MAX = 2;

/** Waits for the first largest-contentful-paint (or a fallback), then 2 s more. */
function afterFirstPaint(run: () => void): () => void {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const go = () => {
    if (done) return;
    done = true;
    timer = setTimeout(run, START_AFTER_LCP_MS);
  };
  let observer: PerformanceObserver | undefined;
  try {
    observer = new PerformanceObserver((list) => {
      if (list.getEntries().length) go();
    });
    observer.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // no LCP entries in this browser
  }
  const fallback = setTimeout(go, FALLBACK_START_MS);
  return () => {
    done = true;
    observer?.disconnect();
    clearTimeout(fallback);
    if (timer) clearTimeout(timer);
  };
}

const idle = (fn: () => void): (() => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (h: number) => void };
  if (w.requestIdleCallback) {
    const h = w.requestIdleCallback(fn, { timeout: 5000 });
    return () => w.cancelIdleCallback?.(h);
  }
  const t = setTimeout(fn, 300);
  return () => clearTimeout(t);
};

/**
 * Warms up to two desks once per signed-in person. `warm(pageKey, space)` is KeptPages' warm (it refuses on devices that cannot spare it
 * and for desks already kept); `enabled` is false under the os/legacy frames and while a desk is the screen being opened anyway.
 */
export function useDeskWarmup(warm: (pageKey: string, space: string | null) => void, enabled: boolean): void {
  const api = useApi();
  const { user, isAuthenticated } = useAuth();
  const { desks: pinned } = usePinnedDesks({ enabled });
  const done = useRef<string | null>(null);
  const lastKey = useRef(0);
  const latest = useRef({ warm, pinned });
  latest.current = { warm, pinned };

  useEffect(() => {
    const onKey = () => { lastKey.current = Date.now(); };
    window.addEventListener("keydown", onKey, { capture: true, passive: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  useEffect(() => {
    const uid = user?.id ?? null;
    if (!enabled || !isAuthenticated || !uid || done.current === uid) return;
    let cancelIdle: (() => void) | undefined;
    let live = true;
    const start = () => {
      const attempt = () => {
        if (!live) return;
        // Not while the person types: try again a little later.
        if (Date.now() - lastKey.current < TYPING_PAUSE_MS) {
          const t = setTimeout(() => { cancelIdle = idle(attempt); }, TYPING_PAUSE_MS);
          cancelIdle = () => clearTimeout(t);
          return;
        }
        done.current = uid;
        void pick().then((list) => {
          if (!live) return;
          for (const d of list.slice(0, WARM_MAX)) latest.current.warm(d.pageKey, d.space);
        });
      };
      cancelIdle = idle(attempt);
    };
    const pick = async (): Promise<RecentDesk[]> => {
      const out: RecentDesk[] = [];
      const seen = new Set<string>();
      const add = (d: RecentDesk) => {
        if (!d.pageKey || seen.has(d.pageKey)) return;
        seen.add(d.pageKey);
        out.push(d);
      };
      for (const p of latest.current.pinned.slice(0, 6)) add({ pageKey: p.pageKey, space: p.space });
      if (out.length < WARM_MAX) {
        try {
          const recent = await api.get<RecentDesk[] | { items?: RecentDesk[] }>("/api/econic/me/recent", { kind: "desk", take: String(WARM_MAX) });
          for (const r of Array.isArray(recent) ? recent : recent.items ?? []) add({ pageKey: r.pageKey, space: r.space ?? null });
        } catch {
          // no recent list: nothing to warm
        }
      }
      return out;
    };
    const stop = afterFirstPaint(start);
    return () => {
      live = false;
      stop();
      cancelIdle?.();
    };
  }, [enabled, isAuthenticated, user?.id, api]);
}

